import { getStoredRole } from "auth/session";
import styled from "@emotion/styled";
import { Icon } from "@iconify/react";
import { Checkbox } from "@mui/material";
import Modal from "@mui/material/Modal";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  DataTable,
  DataTableBody,
  DataTableHeader,
  ExpandableTableRow,
  Input,
  Select,
  TableBodyWithExpandableRow,
  TableHeaderWithBg,
  TableWithExpandableRow,
  ToggleButtonGroup,
} from "../../components";
import { Departments, expenseTypes, paymentMethodOptions, UserRoles } from "../../lib/constants";
import { capitalizeString, formatDate, sortData } from "../../lib/utils";
import { useAccounts } from "../../services";

export default function DebitsPage() {
  const navigate = useNavigate();
  const getUserRole = useCallback(() => {
    return getStoredRole();
  }, []);

  const [showDebitNoteModal, setShowDebitNoteModal] = useState(false);
  const [selectedModalFormValues, setSelectedModalFormValues] = useState();
  const [modalAction, setModalAction] = useState("");

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== UserRoles.ACCOUNTS) {
      toast.error("You are not authorized to access this page.");
      navigate("/");
    }
  }, [getUserRole, navigate]);

  const handleSetShowModal = (formValues, action) => {
    setSelectedModalFormValues(formValues);
    setShowDebitNoteModal(true);
    setModalAction(action);
  };

  return (
    <>
      <div className="row">
        <h2 className="page_title">Debits</h2>
      </div>

      <SalariesSection setShowModal={handleSetShowModal} />
      <BillingSection setShowModal={handleSetShowModal} />
      <VouchersSection setShowModal={handleSetShowModal} />
      <DebitNoteModal
        open={showDebitNoteModal}
        onClose={() => setShowDebitNoteModal(false)}
        formValues={selectedModalFormValues}
        action={modalAction}
      />
    </>
  );
}

const tableHeaders = {
  salaries: [
    { id: "timeline", label: "Month, Year" },
    { id: "total_amount", label: "Total Amount" },
    { id: "action", label: "view/download CSV" },
    { id: "payment_proof", label: "Payment Proof" },
  ],
  bills: [
    { id: "date", label: "Date of payment" },
    { id: "request_type", label: "Request Type" },
    { id: "amount_requested", label: "Amount" },
    { id: "payment_method", label: "Method of payment" },
    { id: "bill_proof", label: "Bill Proof" },
    { id: "receipt_proof", label: "Receipt Proof" },
  ],
  vouchers: [
    { id: "date", label: "Date" },
    { id: "employee_name", label: "Name" },
    { id: "department", label: "Department" },
    { id: "request_type", label: "Request Type" },
    { id: "amount_requested", label: "Amount" },
    { id: "status", label: "Status" },
    { id: "action", label: "" },
  ],
};

const SalariesSection = ({ setShowModal }) => {
  const { getAllSalaryRecordsByMonth } = useAccounts();
  const [salariesData, setSalariesData] = useState([]);
  const [loadStatus, setLoadStatus] = useState({ loading: false, error: null });
  const [sortConfig, setSortConfig] = useState<{key: string; direction: 'asc'|'desc'}> ({ key: "", direction: "asc" });
  const [selectedTimeFrame, setSelectedTimeFrame] = useState({ month: null, year: null });

  const fetchSalariesData = useCallback(async () => {
    setLoadStatus({ loading: true, error: null });
    const { salaryRecords, error } = (await getAllSalaryRecordsByMonth(selectedTimeFrame.month, selectedTimeFrame.year)) as any;
    if (error) {
      setLoadStatus({ loading: false, error });
      toast.error(error);
      setSalariesData([]);
    } else {
      setSalariesData(salaryRecords);
      setLoadStatus({ loading: false, error: null });
    }
  }, [getAllSalaryRecordsByMonth, selectedTimeFrame]);

  const handleSort = useCallback(
    (key) => {
      const { newConfig, sortedData } = sortData(salariesData, key, sortConfig as any);
      setSortConfig(newConfig);
      setSalariesData(sortedData);
    },
    [salariesData, sortConfig]
  );

  useEffect(() => {
    const today = new Date();
    const prevMonth = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
    const prevYear = prevMonth === 11 ? today.getFullYear() - 1 : today.getFullYear();
    // + 1 because month index starts from 0
    setSelectedTimeFrame({ month: prevMonth + 1, year: prevYear });
  }, []);

  useEffect(() => {
    fetchSalariesData();
  }, [fetchSalariesData]);

  return (
    <section className="column spread">
      <div className="row">
        <div className="row start spread">
          <h3 className="font-md">Salaries</h3>
        </div>
        <div className="row end">
          <button
            type="button"
            className="btn cta_outline_btn"
            onClick={() => {
              setShowModal({ debit_type: "salaries" }, "salaries");
            }}
          >
            Create Salary Debit Note
          </button>
        </div>
      </div>
      <DataTable columnCount={tableHeaders.salaries.length} loading={loadStatus.loading}>
        <DataTableHeader
          columns={tableHeaders.salaries}
          sortConfig={sortConfig}
          onSortClick={(key) => handleSort(key)}
        />
        <DataTableBody loading={loadStatus.loading}>
          {salariesData?.map((item, index) => (
            <div key={index} className="data_table_row align-center">
              {tableHeaders.salaries.map((col) => (
                <div key={col.id} className="data_table_cell">
                  {col.id === "action" ? (
                    <div className="row center shrink">
                      <Link to={item.csv} target="_blank" className="btn ghost_btn">
                        <Icon icon="lsicon:view-outline" className="icon" />
                        View
                      </Link>
                      <button type="button" className="btn ghost_btn">
                        <Icon icon="tabler:download" className="icon" />
                        Download
                      </button>
                    </div>
                  ) : col.id === "payment_proof" ? (
                    <Link to={item.payment_proof} target="_blank" className="btn ghost_btn">
                      View
                    </Link>
                  ) : (
                    item[col.id]
                  )}
                </div>
              ))}
            </div>
          ))}
        </DataTableBody>
      </DataTable>
    </section>
  );
};

const BillingSection = ({ setShowModal }) => {
  const { getAllBillRecords } = useAccounts();
  const [billsData, setBillsData] = useState([]);
  const [loadStatus, setLoadStatus] = useState({ loading: false, error: null });
  const [sortConfig, setSortConfig] = useState<{key: string; direction: 'asc'|'desc'}> ({ key: "", direction: "asc" });
  const [expandedRowId, setExpandedRowId] = useState(0);

  const fetchBillsData = useCallback(async () => {
    setLoadStatus({ loading: true, error: null });
    const { billRecords, error } = (await getAllBillRecords()) as any;
    if (error) setLoadStatus({ loading: false, error });
    else {
      setBillsData(billRecords);
      setLoadStatus({ loading: false, error: null });
    }
  }, [getAllBillRecords]);

  const handleSort = useCallback(
    (key) => {
      const { newConfig, sortedData } = sortData(billsData, key, sortConfig as any);
      setSortConfig(newConfig);
      setBillsData(sortedData);
    },
    [sortConfig, billsData]
  );

  useEffect(() => {
    fetchBillsData();
  }, [fetchBillsData]);

  if (loadStatus.error) {
    toast.error(loadStatus.error);
    setBillsData({ active: [], approved: [], rejected: [] } as any);
  }

  return (
    <section className="column spread">
      <div className="row">
        <h3 className="font-md">Billings</h3>
        <button
          type="button"
          className="btn cta_outline_btn"
          onClick={() => setShowModal({ debit_type: "billing" }, "billing")}
        >
          Create Billing Debit Note
        </button>
      </div>

      <DataTable columnCount={tableHeaders.bills.length} loading={loadStatus.loading}>
        <DataTableHeader
          columns={tableHeaders.bills}
          sortConfig={sortConfig}
          onSortClick={(key) => handleSort(key)}
          disabled={billsData?.length === 0}
        />
        <DataTableBody loading={loadStatus.loading}>
          {billsData?.map((item) => (
            <div className="data_table_row" key={item.id}>
              {tableHeaders.bills.map((col) => (
                <div key={col.id} className="data_table_cell">
                  {col.id === "date" ? (
                    formatDate(item[col.id])
                  ) : ["bill_proof", "receipt_proof"].includes(col.id) ? (
                    <Link to={item[col.id]} target="_blank" className="btn ghost_btn">
                      View
                    </Link>
                  ) : (
                    item[col.id]
                  )}
                </div>
              ))}
            </div>
            // <DataTableRow
            //   key={item.id}
            //   columns={tableHeaders.bills}
            //   data={item}
            //   isExpanded={expandedRowId === index}
            //   setExpandedRowId={setExpandedRowId}
            //   onExpandClick={() => setExpandedRowId(expandedRowId === index ? null : index)}
            //   highlightStatus={true}
            // >
            //   <div className="column">
            //     <div className="row">
            //       <div className="row">
            //         <span className="label">Note</span>
            //         <span className="value">{item.note}</span>
            //       </div>
            //       <button type="button" className="btn cta_outline_btn" onClick={() => setShowModal()}>
            //         Create Debit Note
            //       </button>
            //     </div>
            //     <div className="row">
            //       <div className="row">
            //         <span className="label">Applied On:</span>
            //         <span className="value font-md">{item.applied_on}</span>
            //       </div>
            //     </div>
            //   </div>
            // </DataTableRow>
          ))}
        </DataTableBody>
      </DataTable>
    </section>
  );
};

const VouchersSection = ({ setShowModal }) => {
  const { getAllVouchersData } = useAccounts();
  const [params, setParams] = useSearchParams();
  const [voucherStatus, setVoucherStatus] = useState("active");
  const [vouchersData, setVouchersData] = useState({ active: [], closed: [] });
  const [loadStatus, setLoadStatus] = useState({ loading: false, error: null });
  const [sortConfig, setSortConfig] = useState<{key: string; direction: 'asc'|'desc'}> ({ key: "", direction: "asc" });
  const [expandedRowId, setExpandedRowId] = useState(0);

  const employeeOptions = useMemo(
    () =>
      (vouchersData?.length &&
        vouchersData?.map((voucher) => ({ label: voucher.employee_name, value: voucher.employee_id }))) ||
      [],
    [vouchersData]
  );

  const fetchVouchersData = useCallback(async () => {
    setLoadStatus({ loading: true, error: null });
    const { vouchers, error } = (await getAllVouchersData()) as any;
    if (error) {
      setLoadStatus({ loading: false, error });
      return;
    }
    setLoadStatus({ loading: false, error: null });
    const categorizedVouchers = vouchers?.reduce(
      (acc, voucher) => {
        if (!voucher.isReimbursed) acc.active.push(voucher);
        else acc.closed.push(voucher);
        return acc;
      },
      { active: [], closed: [] }
    );
    setVouchersData(categorizedVouchers);
  }, [getAllVouchersData]);

  const handleSort = useCallback(
    (key) => {
      const { newConfig, sortedData } = sortData(vouchersData as any, key, sortConfig as any);
      setSortConfig(newConfig);
      setVouchersData(sortedData as any);
    },
    [vouchersData, sortConfig]
  );

  const handleFilterVouchers = (status) => {
    setVoucherStatus(status);
    const billStatus = params.get("bills");
    if (billStatus) setParams({ vouchers: status, bills: billStatus });
    else setParams({ vouchers: status });
  };

  useEffect(() => {
    fetchVouchersData();
  }, [fetchVouchersData]);

  useEffect(() => {
    setVoucherStatus(params.get("vouchers") || "active");
  }, [params]);

  if (loadStatus.error) {
    toast.error(loadStatus.error);
    setVouchersData({ active: [], approved: [] } as any);
  }

  return (
    <section className="column spread">
      <div className="row">
        <h3 className="font-md">Vouchers & Office Supplies Requests</h3>
        <button type="button" className="btn cta_btn" onClick={() => setShowModal(true)}>
          + Add Request
        </button>
      </div>
      <section className="column spread">
        <ToggleButtonGroup labels={["active", "closed"]} onClick={handleFilterVouchers} currentValue={voucherStatus} />
      </section>
      <TableWithExpandableRow columnCount={tableHeaders.vouchers.length} loading={loadStatus.loading}>
        <TableHeaderWithBg
          columns={tableHeaders.vouchers}
          sortConfig={sortConfig}
          onSortClick={(key) => handleSort(key)}
          disabled={vouchersData[voucherStatus]?.length === 0}
        />
        <TableBodyWithExpandableRow columnCount={tableHeaders.vouchers.length} loading={loadStatus.loading}>
          {vouchersData?.[voucherStatus]?.map((item, index) => (
            <ExpandableTableRow
              key={item.id}
              columns={tableHeaders.vouchers}
              data={item}
              isExpanded={expandedRowId === index}
              onExpandClick={() => setExpandedRowId(expandedRowId === index ? null : index)}
              highlightStatus={true}
            >
              <div className="column">
                <div className="row">
                  <div className="row">
                    <span className="label">Note</span>
                    <span className="value">{item.note}</span>
                  </div>
                  {!item.isReimbursed && (
                    <button
                      type="button"
                      className="btn cta_outline_btn"
                      onClick={() =>
                        setShowModal(
                          {
                            debit_type: "reimbursements",
                            employee_id: item.employee_id,
                            employee_name: item.employee_name,
                            department: item.department,
                            request_type: item.request_type,
                            amount: `${item.amount_requested}`.replaceAll(",", "").replace("₹", ""),
                            ...item,
                            employeeOptions,
                          },
                          "reimbursements"
                        )
                      }
                    >
                      Create Voucher Debit Note
                    </button>
                  )}
                </div>
                <div className="row start">
                  <span className="label">Applied On:</span>
                  <span className="value font-md">{formatDate(item.applied_on)}</span>
                </div>
              </div>
            </ExpandableTableRow>
          ))}
        </TableBodyWithExpandableRow>
      </TableWithExpandableRow>
    </section>
  );
};

const ModalWrapper = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const debitTypeOptions = [
  { label: "Salaries", value: "salaries" },
  { label: "Advance salary", value: "advance_salary" },
  { label: "Bonus", value: "bonus" },
  { label: "Billing Requests", value: "billing" },
  { label: "Vouchers & Office supplies requests", value: "reimbursements" },
];

const voucherTypeOptions = expenseTypes.map((type) => ({
  label: type,
  value: type,
}));

const billTypeOptions = [
  "Funds",
  "Monthly rent",
  "EMI",
  "Maintenance",
  "Taxes & Insurance",
  "Electricity bill",
  "Internet",
].map((type) => ({
  label: type,
  value: type,
}));

const DebitNoteModal = ({ open, onClose, formValues, action }) => {
  const initialSalariesFormData = useMemo(() => ({ all_employees: false, employee_ids: [] }), []);

  const initialReimbursementsFormData = useMemo(
    () => ({
      employee_id: "",
      department: "",
      request_type: "",
      amount: "",
    }),
    []
  );

  // Memoized like its two siblings above: as a bare object literal this was rebuilt every
  // render, so the `initialFormData` memo below that depends on it never actually held.
  const initialBillsFormData = useMemo(() => ({ request_type: "", amount: "" }), []);

  const initialFormData = useMemo(
    () => ({
      debit_type: "",
      payment_method: "",
      payment_receipt: null,
      note: "",
      ...initialSalariesFormData,
      ...initialReimbursementsFormData,
      ...initialBillsFormData,
    }),
    [initialBillsFormData, initialReimbursementsFormData, initialSalariesFormData]
  );
  const [formData, setFormData] = useState({ ...initialFormData, ...formValues });
  const [debitType, setDebitType] = useState(formValues?.debit_type ?? "");
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeeIdsInput, setEmployeeIdsInput] = useState("");
  const [isRequestTypeOther, setIsRequestTypeOther] = useState(false);
  const [debitOptions, setDebitOptions] = useState<any[]>([]);

  const handleClose = () => {
    setFormData(initialFormData);
    onClose();
  };

  const onSubmit = (e) => {
    e.preventDefault();
    // add form submission logic here
    toast.info("Debit note created successfully");
    handleClose();
  };

  useEffect(() => {
    if (action === "salaries") {
      setDebitOptions([
        { label: "Salaries", value: "salaries" },
        { label: "Advance salary", value: "advance_salary" },
        { label: "Bonus", value: "bonus" },
      ]);
    } else if (action === "billing") {
      setDebitOptions([{ label: "Billing Requests", value: "billing" }]);
    } else if (action === "reimbursements") {
      setDebitOptions([{ label: "Vouchers & Office supplies requests", value: "reimbursements" }]);
    } else {
      setDebitOptions(debitTypeOptions);
    }
  }, [action]);

  const handleFileChange = (e, name) => {
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, [name]: file }));
  };

  useEffect(() => {
    if (formValues) {
      setFormData((prev) => ({ ...prev, ...formValues }));
      setDebitType(formValues.debit_type ?? "");
      setEmployeeOptions(formValues.employeeOptions ?? []);
    }
  }, [formValues]);

  const renderDebitTypeSelect = () => (
    <Select
      name="debit_type"
      id="debit_type"
      placeholder="Select debit type"
      options={debitOptions}
      required
      value={debitTypeOptions.find((item) => item.value === debitType)?.label ?? debitType}
      onChange={(value) => {
        setDebitType(value);
        setFormData({ ...formData, debit_type: value });
      }}
    />
  );

  const renderPaymentMethodSelect = () => (
    <Select
      name="payment_method"
      id="payment_method"
      placeholder="Method of payment"
      options={paymentMethodOptions}
      required
      value={formData.payment_method}
      onChange={(value) => setFormData((prev) => ({ ...prev, payment_method: value }))}
    />
  );

  const renderPaymentScreenshotInput = () => (
    <div className="row">
      <span className="label gray">Payment Receipt</span>
      <div className="row end">
        {formData.payment_receipt ? (
          <Link to={URL.createObjectURL(formData.payment_receipt)}>preview</Link>
        ) : (
          <span className="gray">No screenshot uploaded</span>
        )}
        <label htmlFor="payment_receipt" className="btn cta_outline_btn">
          <span>Upload Screenshot</span>
          <input
            type="file"
            onChange={(e) => handleFileChange(e, "payment_receipt")}
            accept=".jpg, .jpeg, .png"
            hidden
            id="payment_receipt"
          />
        </label>
      </div>
    </div>
  );

  const renderBillScreenshotInput = () =>
    formData.debit_type === "billing" ? (
      <div className="row">
        <span className="label gray">Bill</span>
        <div className="row end">
          {formData.bill_screenshot ? (
            <Link to={URL.createObjectURL(formData.bill_screenshot)}>preview</Link>
          ) : (
            <span className="gray">No screenshot uploaded</span>
          )}
          <label htmlFor="bill_screenshot" className="btn cta_outline_btn">
            <span>Upload Screenshot</span>
            <input
              type="file"
              onChange={(e) => handleFileChange(e, "bill_screenshot")}
              accept=".jpg, .jpeg, .png"
              hidden
              id="bill_screenshot"
            />
          </label>
        </div>
      </div>
    ) : null;

  const renderNoteInput = () => (
    <input
      type="text"
      required
      onChange={(e) => setFormData((prev) => ({ ...prev, note: e.target.value }))}
      className="input"
      placeholder="Note/Comment"
    />
  );

  const renderRequestTypeInput = (options) => (
    <div className="row">
      <Select
        name="request_type"
        id="request_type"
        placeholder="Request type"
        options={options}
        required
        value={options.find((item) => item.value === formData.request_type)?.label ?? formData.request_type}
        onChange={(value) => {
          if (value === "other") {
            setIsRequestTypeOther(true);
            return;
          }
          setIsRequestTypeOther(false);
          setFormData((prev) => ({ ...prev, request_type: value }));
        }}
        full={!isRequestTypeOther}
      />
      {isRequestTypeOther && (
        <input
          type="text"
          required
          onChange={(e) => setFormData((prev) => ({ ...prev, request_type: e.target.value }))}
          className="input full"
          placeholder="Enter request type"
        />
      )}
    </div>
  );

  const renderAmountInput = () => (
    <Input
      type="number"
      name="amount"
      id="amount"
      placeholder="Amount (₹)"
      prefix="₹"
      value={formData.amount}
      onChange={(e) => setFormData((prev) => ({ ...prev, amount: +e.target.value }))}
    />
  );

  const renderSalariesFormSection = () => (
    <>
      <div className="row start">
        <label htmlFor="all_employees" className="row gray">
          <Checkbox
            id="all_employees"
            checked={formData.all_employees}
            onChange={() => setFormData((prev) => ({ ...prev, all_employees: !prev.all_employees }))}
          />
          {/* <input
            type="checkbox"
            name="all_employees"
            id="all_employees"
            checked={formData.all_employees}
            onChange={() => setFormData((prev) => ({ ...prev, all_employees: !prev.all_employees }))}
          /> */}
          All Employees
        </label>
        <span className="gray small">or</span>
        <input
          type="text"
          name="employee_ids"
          className="input full"
          placeholder="Enter Employee ID (Enter one or multiple employees ID)"
          onChange={(e) => {
            const inputValue = e.target.value;
            setEmployeeIdsInput(inputValue);

            // Split the input value by comma or space
            // The input can be '123, 456, 789' or '123 456 789' or '123,456,789' or '123, 456 789'
            const employeeIdArr = inputValue.split(/[ ,]+/);
            setFormData((prev) => ({ ...prev, employee_ids: employeeIdArr }));
          }}
          value={employeeIdsInput}
          required={!formData.all_employees}
          disabled={formData.all_employees}
        />
      </div>
    </>
  );

  const renderReimbursementsFormSection = () => (
    <>
      <Select
        name="employee"
        id="employee"
        placeholder="Employee Name"
        options={Array.from({ length: 20 }).map((_, idx) => ({
          label: `Employee ${idx + 1}`,
          value: `Employee ${idx + 1}`,
        }))}
        required
        value={employeeOptions.find((employee) => employee.id === formData.employee_id)?.name ?? formData.employee_name}
        onChange={(value) => {
          const selectedEmployee = employeeOptions.find((employee) => employee.id === value);
          setFormData((prev) => ({ ...prev, employee_id: value, department: selectedEmployee.department }));
        }}
      />
      <Select
        name="department"
        id="department"
        placeholder="Department"
        value={capitalizeString(formData.department)}
        options={[...Object.values(Departments), "Accounts", "HR"].map((department) => ({
          label: capitalizeString(department),
          value: capitalizeString(department),
        }))}
        onChange={(value) => setFormData((prev) => ({ ...prev, department: value, employee_id: null }))}
      />
      {renderRequestTypeInput([...voucherTypeOptions, { label: "Other", value: "other" }])}
      {renderAmountInput()}
    </>
  );

  const renderBillsFormSection = () => (
    <>
      <input
        type="date"
        name="date"
        id="date"
        required
        value={formData.date}
        className="input"
        onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
      />
      {renderRequestTypeInput(billTypeOptions)}
      {renderAmountInput()}
    </>
  );

  const renderSubmitButton = () => (
    <div className="row end">
      <button type="submit" form="debit-note-form" className="btn cta_btn">
        Submit
      </button>
    </div>
  );

  const formSection = {
    salaries: renderSalariesFormSection(),
    reimbursements: renderReimbursementsFormSection(),
    billing: renderBillsFormSection(),
    bonus: renderSalariesFormSection(),
    advance_salary: renderSalariesFormSection(),
  };

  return (
    <ModalWrapper
      open={open}
      onClose={handleClose}
      aria-labelledby="add-debit-modal"
      aria-describedby="add-debit-modal"
    >
      <div className="modal_container full column">
        <h3 className="section_title">Create debit note</h3>
        <form id="debit-note-form" className="column small" onSubmit={onSubmit}>
          {renderDebitTypeSelect()}
          {formSection[debitType]}
          {renderPaymentMethodSelect()}
          {renderPaymentScreenshotInput()}
          {renderBillScreenshotInput()}
          {/* {renderNoteInput()} */}
          {renderSubmitButton()}
        </form>
      </div>
    </ModalWrapper>
  );
};
